/**
 * Bridges the nested table's column model onto the GENERIC cell configuration
 * (chart-components/unified-table-matrix-chart/table-cell-config.ts), so the
 * nested table renders its cells through the SAME resolver as the Normal /
 * Editable / Drill-Down tables.
 *
 * A nested column can carry the shared `cellConfig` directly (what the shared
 * editor writes) AND the legacy per-column keys it always had (`color`, `bold`,
 * `colorRules`, `badge` / `badgeStyles`, `options`, `placeholder`, `prefix` /
 * `suffix`). `nestedColumnCellConfig` folds the legacy keys into a config and
 * lets the explicit `cellConfig` win, so old designs keep rendering as they did
 * and new ones get every shared feature (data bars, heat maps, trend arrows,
 * cross-column rules, tooltips, highlight…).
 */

import {
  isEmptyTableCellConfig,
  normalizeTableCellConfig,
  type TableCellConfig,
  type TableCellRule,
  type TableCellRuleOp,
} from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import type { NestedColorOp, NestedOption, NestedTableColumn } from './nested-table.types';

const LEGACY_OPS: Record<NestedColorOp, TableCellRuleOp> = {
  gt: '>', gte: '>=', lt: '<', lte: '<=', eq: '=', neq: '!=', empty: 'empty', notEmpty: 'notEmpty', contains: 'contains',
};

/** The effective cell configuration of a nested column (undefined = plain). */
export function nestedColumnCellConfig(column: NestedTableColumn): TableCellConfig | undefined {
  const legacy: TableCellConfig = {};
  if (column.color) legacy.textColor = column.color;
  if (column.bold) legacy.bold = true;
  const rules: TableCellRule[] = (column.colorRules ?? [])
    .filter((rule) => rule && LEGACY_OPS[rule.op])
    .map((rule) => ({
      op: LEGACY_OPS[rule.op],
      ...(rule.value !== undefined && rule.value !== null && rule.value !== '' ? { value: typeof rule.value === 'number' ? rule.value : String(rule.value) } : {}),
      ...(rule.color ? { color: rule.color } : {}),
      ...(rule.background ? { background: rule.background } : {}),
      ...(rule.bold ? { bold: true } : {}),
    }));
  // Per-value badge styles are equality rules on the badge value.
  if (column.type === 'badge' && column.badgeStyles) {
    Object.entries(column.badgeStyles).forEach(([value, style]) => {
      if (!style) return;
      rules.push({ op: '=', value, ...(style.color ? { color: style.color } : {}), ...(style.background ? { background: style.background } : {}) });
    });
  }
  if (rules.length) legacy.rules = rules;
  if (column.placeholder) legacy.placeholder = column.placeholder;
  const options = (column.options ?? []).map((option) => (typeof option === 'string' ? option : (option as NestedOption).value)).filter(Boolean);
  if (options.length) legacy.options = options;

  const explicit = normalizeTableCellConfig(column.cellConfig);
  if (!explicit) return isEmptyTableCellConfig(legacy) ? undefined : legacy;
  // Explicit config wins key by key; legacy rules run AFTER the explicit ones.
  const merged: TableCellConfig = { ...legacy, ...explicit };
  const explicitRules = explicit.rules ?? [];
  const legacyRules = legacy.rules ?? [];
  if (explicitRules.length || legacyRules.length) merged.rules = [...explicitRules, ...legacyRules];
  if (!explicit.options?.length && legacy.options?.length) merged.options = legacy.options;
  return merged;
}

/** How a nested cell renders: the shared display kinds + the nested editors. */
export type NestedCellKind = 'badge' | 'input' | 'dropdown' | 'databar' | 'progress' | 'trend' | 'icon' | 'link' | 'buttons' | 'default';

export function nestedCellKind(column: NestedTableColumn, cfg: TableCellConfig | undefined): NestedCellKind {
  // An editor declared on the shared config turns the cell into an input /
  // dropdown even when the column's own type is plain.
  // Buttons replace the value entirely.
  if (cfg?.display === 'buttons' && cfg.buttons?.length) return 'buttons';
  const editor = cfg?.editor;
  if (editor === 'dropdown') return 'dropdown';
  if (editor && editor !== 'none') return 'input';
  if (column.type === 'input' || column.type === 'dropdown') return column.type;
  const display = cfg?.display;
  if (display === 'badge' || column.type === 'badge') return 'badge';
  if (display === 'databar' || display === 'progress' || display === 'trend' || display === 'icon' || display === 'link') return display;
  return 'default';
}
